import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useZxing, type Result } from "react-zxing";
import { MAIN_COLOR_RED } from "../assets/styles";

let sharedAudioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
    if (typeof window === "undefined") return null;
    try {
        const AC =
            window.AudioContext ||
            (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AC) return null;
        if (!sharedAudioContext || sharedAudioContext.state === "closed") {
            sharedAudioContext = new AC();
        }
        return sharedAudioContext;
    } catch {
        return null;
    }
}

/** Short success chirp (no audio file; works offline). */
function playBarcodeScanBeep(): void {
    const ctx = getAudioContext();
    if (!ctx) return;
    const t = ctx.currentTime;
    try {
        if (ctx.state === "suspended") {
            void ctx.resume();
        }
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.frequency.setValueAtTime(1200, t);
        osc.frequency.exponentialRampToValueAtTime(2200, t + 0.07);

        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.14, t + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);

        osc.start(t);
        osc.stop(t + 0.15);
    } catch {
        /* ignore */
    }
}

/** Min ms between beeps (ZXing can decode the same frame many times). */
const BEEP_THROTTLE_MS = 400;

interface BarcodeScannerProps {
    onScan: (barcode: string) => void;
    onClose: () => void;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ onScan, onClose }) => {
    const [lastResult, setLastResult] = useState("");
    const [cameraPaused, setCameraPaused] = useState(false);

    const onScanRef = useRef(onScan);
    onScanRef.current = onScan;

    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const closeNotifiedRef = useRef(false);
    const lastBeepAtRef = useRef(0);

    const zxingOptions = useMemo(
        () => ({
            paused: cameraPaused,
            onDecodeResult(result: Result) {
                const text = result.getText();
                const now = typeof performance !== "undefined" ? performance.now() : Date.now();
                if (now - lastBeepAtRef.current >= BEEP_THROTTLE_MS) {
                    lastBeepAtRef.current = now;
                    playBarcodeScanBeep();
                }
                setLastResult(text);
                onScanRef.current(text);
            },
        }),
        [cameraPaused]
    );

    const { ref } = useZxing(zxingOptions);

    useEffect(() => {
        if (!cameraPaused || closeNotifiedRef.current) return;
        closeNotifiedRef.current = true;
        onCloseRef.current();
    }, [cameraPaused]);

    const handleCloseClick = useCallback(() => {
        setCameraPaused(true);
    }, []);

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
                backgroundColor: "rgba(0, 0, 0, 0.9)",
            }}
        >
            <Paper
                sx={{
                    width: "100%",
                    maxWidth: 500,
                    maxHeight: "90vh",
                    overflow: "hidden",
                    borderRadius: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.5,
                        backgroundColor: MAIN_COLOR_RED,
                    }}
                >
                    <Typography variant="h6" component="h2" sx={{ color: "#fff" }}>
                        Scan barcode
                    </Typography>
                    <IconButton
                        onClick={handleCloseClick}
                        aria-label="Close scanner"
                        sx={{ color: "#fff" }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ position: "relative", width: "100%", height: 300, bgcolor: "#000" }}>
                    {/* No autoPlay — ZXing calls play(); avoids duplicate play warnings */}
                    <video
                        ref={ref}
                        muted
                        playsInline
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 220,
                            height: 110,
                            border: "2px solid #fff",
                            borderRadius: 1,
                            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.45)",
                            pointerEvents: "none",
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            position: "absolute",
                            bottom: 12,
                            left: 12,
                            right: 12,
                            textAlign: "center",
                            color: "#fff",
                            bgcolor: "rgba(0,0,0,0.65)",
                            py: 1,
                            px: 1,
                            borderRadius: 1,
                        }}
                    >
                        Point the barcode inside the frame
                    </Typography>
                </Box>

                <Box sx={{ px: 2, py: 1.5, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: lastResult ? 1 : 0 }}>
                        Scanning…
                    </Typography>
                    {lastResult ? (
                        <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                            Last read: <strong>{lastResult}</strong>
                        </Typography>
                    ) : null}
                    <Button
                        variant="outlined"
                        fullWidth
                        sx={{ mt: 1.5 }}
                        onClick={handleCloseClick}
                    >
                        Cancel
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default BarcodeScanner;
